import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Group, Segment } from '@core/interfaces';
import { chunk, find, orderBy, sumBy } from 'lodash';
import { sentry } from 'sentry';
import { GroupService } from '../../../services/groups-api.service';

@Component({
  selector: 'tab-segments',
  templateUrl: './tab-segments.component.html',
  styleUrls: ['./tab-segments.component.scss']
})
export class TabSegmentsComponent implements OnChanges, OnInit {

  @Input()
  public isViewMode: boolean;

  @Input()
  public group: Group;

  public isLoading = true;
  public segments: Segment[] = [];
  public isAllSelected = false;
  public isSaving: boolean[] = [];
  public linkedSegmentsCount = 0;

  constructor(
    private readonly groupService: GroupService
  ) {}

  public async ngOnInit(): Promise<void> {
    this.segments = await this.searchSegments();
    this.isAllSelected = this.checkIfIsAllSelected();
    this.linkedSegmentsCount = this.countLinkedSegments();
    this.isLoading = false;
  }

  countLinkedSegments(): any {
    return sumBy(this.segments, (segment) => {
      return segment.groupsConnection.items.some((group) => group.id === this.group.id) ? 1 : 0;
    });
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes.group) {
      this.segments = await this.searchSegments();
      this.isAllSelected = this.checkIfIsAllSelected();
      this.linkedSegmentsCount = this.countLinkedSegments();
    }
  }

  private async searchSegments(): Promise<Segment[] | null> {
    if (!this.group.id) {
      return null;
    }

    const segments = [];
    let totalCount = null;
    let totalSegmentsFetched = 0;
    let offset = 0;
    const limit = 100;
    do {
      const page = await this.groupService.listSegments({
        groupId: this.group.id,
        limit: limit,
        offset: offset,
      });

      for (const segment of page.items) {
        if (segment.archived && segment.groupsConnection.items.length === 0) { continue; }
        segments.push(segment);
      }

      totalSegmentsFetched += segments.length;
      totalCount = page.totalCount;
      offset += limit;
    } while (totalCount > totalSegmentsFetched);

    return orderBy(segments, ['archived', 'name']);
  }

  public async toggleAll(checked: boolean): Promise<void> {
    const segmentsToChange = this.segments.filter((segment) => {
      return checked !== !!segment.groupsConnection.items.length;
    });

    await this.toggleSegmentLinkedToGroup(checked, segmentsToChange);
  }

  public async toggleSegmentLinkedToGroup(checked: boolean, segments: Segment[]): Promise<void> {
    this.isSaving.push(true);
    segments.forEach((segment) => segment['isSaving'] = true);

    const groupId = this.group.id;
    const allChangedSegments: Segment[] = [];

    for (const segmentIds of chunk(segments.map((segment) => segment.id), 100)) {
      const promise = checked
        ? this.groupService.addSegmentToGroup({ groupId, segmentIds })
        : this.groupService.removeSegmentFromGroup({ groupId, segmentIds });

      await promise
        .then((changedSegments) => allChangedSegments.push(...changedSegments))
        .catch((err) => { sentry.captureException(err); });
    }

    for (const segment of segments) {
      const changedSegment = find(allChangedSegments, {
        id: segment.id
      });

      if (!changedSegment) {
        continue;
      }

      const currentSegment = find(this.segments, {
        id: segment.id
      });

      currentSegment.groupsConnection.items = changedSegment.groupsConnection.items;
    }

    this.isAllSelected = this.checkIfIsAllSelected();
    this.linkedSegmentsCount = this.countLinkedSegments();
    segments.forEach((segment) => segment['isSaving'] = false);
    this.isSaving.pop();
  }

  private checkIfIsAllSelected(): boolean {
    return this.segments.length && this.segments.every((segment) => segment.groupsConnection.items.length);
  }
}
